import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Question } from "../types.ts";
import type { LoadedCatalog } from "../catalog.ts";
import type { Settings, ProgressMap } from "../state/store.ts";
import { buildTopics, pick } from "../catalog.ts";
import { DEFAULT_CLASS_ID } from "../exam.ts";
import { isFit } from "../sync/types.ts";
import type { Topic } from "../catalog.ts";
import { useKeyboard } from "../state/useKeyboard.ts";
import { t } from "../i18n/ui.ts";
import { QuestionCard } from "./QuestionCard.tsx";
import { QuestionDetail } from "./QuestionDetail.tsx";
import { ExamToolbar } from "./ExamToolbar.tsx";
import type { PracticeMode } from "./HomeView.tsx";

interface Props {
  catalog: LoadedCatalog;
  settings: Settings;
  progress: ProgressMap;
  mode: PracticeMode;
  onModeChange: (mode: PracticeMode) => void;
  /** Official number of the question to show, taken from the URL. */
  question?: string;
  onQuestionChange: (number: string, options?: { replace?: boolean }) => void;
  marked: number[];
  isMarked: (id: number) => boolean;
  onToggleMark: (id: number) => void;
  onRecord: (questionId: number, correct: boolean) => void;
  onExit: () => void;
  onSwapLanguages: () => void;
  onCycleSecondary: () => void;
  onShowHelp: () => void;
}

/** How many questions the "30 new questions" block serves up. */
const NEW_BLOCK_SIZE = 30;

export function PracticeView({
  catalog, settings, progress, mode, onModeChange, question, onQuestionChange,
  marked, isMarked, onToggleMark,
  onRecord, onExit, onSwapLanguages, onCycleSecondary, onShowHelp,
}: Props) {
  const lang = settings.primary;
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  const [typed, setTyped] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [detail, setDetail] = useState(false);
  const [playSignal, setPlaySignal] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);

  // The "30 new questions" block is dealt once, on entry, and then held.
  // Answering a question marks it seen, so a block recomputed from `progress`
  // would reshuffle itself out from under the user after every answer.
  const [newBlock] = useState(() =>
    mode.kind === "new" ? dealNewQuestions(catalog, progress) : null
  );

  const topics = useMemo(() => buildTopics(catalog, lang), [catalog, lang]);

  const pool = useMemo(
    () => buildPool(catalog, topics, mode, progress, marked, query, settings, newBlock),
    [catalog, topics, mode, progress, marked, query, settings, newBlock]
  );

  // The URL names the question; fall back to the first in the pool if it names
  // none, or one that the current filter has excluded.
  const found = pool.findIndex((q) => q.number === question);
  const safeIndex = found >= 0 ? found : 0;
  const current = pool[safeIndex];

  // Keep the address pointing at what is on screen — on arrival, and whenever
  // a filter or search change moves us to a different question.
  useEffect(() => {
    if (!current || current.number === question) return;
    onQuestionChange(current.number, { replace: true });
  }, [current, question, onQuestionChange]);

  const resetAnswer = useCallback(() => {
    setSelected([]);
    setTyped("");
    setRevealed(false);
    setExplaining(false);
    setTranslating(false);
  }, []);

  // Clear the answer whenever the question changes, however that happened —
  // a Next click, a shortcut, or the browser's back button.
  useEffect(() => {
    resetAnswer();
    setDetail(false);
    cardRef.current?.focus();
  }, [safeIndex, current?.id, resetAnswer]);

  const go = useCallback(
    (to: number) => {
      if (pool.length === 0) return;
      const next = ((to % pool.length) + pool.length) % pool.length;
      onQuestionChange(pool[next]!.number);
    },
    [pool, pool.length, onQuestionChange]
  );

  const check = useCallback(() => {
    if (!current) return;
    if (revealed) {
      go(safeIndex + 1);
      return;
    }
    const correct =
      current.kind === "number"
        ? typed.trim().replace(",", ".") === current.numeric?.value
        : sameSet(
            selected,
            current.answers.map((a, i) => (a.correct ? i : -1)).filter((i) => i >= 0)
          );
    setRevealed(true);
    if (settings.autoExplain) setExplaining(true);
    onRecord(current.id, correct);
  }, [current, revealed, typed, selected, safeIndex, go, onRecord, settings.autoExplain]);

  const toggle = useCallback(
    (i: number) => {
      if (revealed) return;
      setSelected((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));
    },
    [revealed]
  );

  useKeyboard({
    arrowright: () => go(safeIndex + 1),
    n: () => go(safeIndex + 1),
    arrowleft: () => go(safeIndex - 1),
    p: () => go(safeIndex - 1),
    enter: check,
    "1": () => current?.answers[0] && toggle(0),
    "2": () => current?.answers[1] && toggle(1),
    "3": () => current?.answers[2] && toggle(2),
    e: () => setExplaining((v) => !v),
    t: () => setTranslating((v) => !v),
    v: () => setPlaySignal((n) => n + 1),
    m: () => current && onToggleMark(current.id),
    s: onSwapLanguages,
    l: onCycleSecondary,
    escape: onExit,
    "?": onShowHelp,
  });

  // Topic picker: shown until a subject is chosen.
  if (mode.kind === "topic" && mode.categoryId === null) {
    return (
      <div className="topics">
        <h2 className="topics__title">{t(lang, "specialTopics")}</h2>
        <ul className="topics__list">
          {topics.map((topic) => {
            const done = topic.questionIds.filter(
              (id) => progress[String(id)]?.lastCorrect === true
            ).length;
            return (
              <li key={topic.id}>
                <button
                  type="button"
                  className="menu__item"
                  onClick={() => onModeChange({ kind: "topic", categoryId: topic.id })}
                >
                  <span className="menu__body">
                    <span className="menu__label">{topic.title}</span>
                    <span className="menu__hint">
                      {t(lang, "topicProgress", {
                        correct: done, total: topic.questionIds.length,
                      })}
                    </span>
                  </span>
                  <span className="menu__chev" aria-hidden="true">›</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const showSearch = mode.kind === "search";

  return (
    <div className="tsheet">
      {showSearch && (
        <label className="tsearch">
          <span className="sr-only">{t(lang, "searchPlaceholder")}</span>
          <input
            type="search"
            value={query}
            placeholder={t(lang, "searchPlaceholder")}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </label>
      )}

      {!current ? (
        <p className="tempty">{emptyMessage(mode, lang, query)}</p>
      ) : (
        <>
          <QuestionCard
            // Remount per question: the film gate and its play count are
            // per-question state.
            key={current.id}
            ref={cardRef}
            question={current}
            settings={settings}
            selected={selected}
            typed={typed}
            onToggle={toggle}
            onType={setTyped}
            revealed={revealed}
            showExplanation={explaining}
            showSecondary={translating}
            positionLabel={`${safeIndex + 1} / ${pool.length}`}
            playSignal={playSignal}
            onClose={onExit}
            onInfo={() => setDetail(true)}
          />

          <QuestionDetail
            open={detail}
            lang={lang}
            question={current}
            catalog={catalog}
            progress={progress[String(current.id)]}
            onClose={() => setDetail(false)}
          />

          <ExamToolbar
            lang={lang}
            basicLabel={current.basic ? t(lang, "basicKnowledge") : ""}
            classLabel={catalog.classes.find((c) => c.id === DEFAULT_CLASS_ID)?.name ?? ""}
            status={t(lang, "questionOf", { i: safeIndex + 1, n: pool.length })}
            marked={isMarked(current.id)}
            onToggleMark={() => onToggleMark(current.id)}
            extraActions={[
              ...(settings.secondary && settings.secondary !== settings.primary
                ? [{
                    label: t(lang, translating ? "hideTranslation" : "showTranslation"),
                    onClick: () => setTranslating((v) => !v),
                    pressed: translating,
                  }]
                : []),
              {
                label: t(lang, explaining ? "hideExplanation" : "showExplanation"),
                onClick: () => setExplaining((v) => !v),
                pressed: explaining,
              },
            ]}
            onPrev={() => go(safeIndex - 1)}
            onNext={check}
            nextLabel={revealed ? t(lang, "next") : t(lang, "checkAnswer")}
            paginator={{ index: safeIndex, total: pool.length, onJump: go }}
          />
        </>
      )}
    </div>
  );
}

function emptyMessage(mode: PracticeMode, lang: Parameters<typeof t>[0], query: string) {
  if (mode.kind === "new") return t(lang, "allFit");
  if (mode.kind === "marked") return t(lang, "noMarked");
  if (mode.kind === "mistakes") return t(lang, "noMistakes");
  if (mode.kind === "search" && !query) return t(lang, "searchPlaceholder");
  return t(lang, "noResults");
}

/**
 * Deals the "30 new questions" block, following upstream's
 * `initQuickstartQuestionArray` step for step.
 *
 * The pool is every question for this licence class that is not yet "fit for
 * the test" — upstream's rule is the last two attempts both correct, so a
 * question keeps coming back until you have got it right twice running. Note
 * this is *not* "never seen": questions you answered once, or got wrong, stay
 * in the pool.
 */
function dealNewQuestions(catalog: LoadedCatalog, progress: ProgressMap): number[] {
  const pool = catalog.questions
    .filter((q) => q.basic || q.classes.includes(DEFAULT_CLASS_ID))
    .filter((q) => !isFit(progress[String(q.id)]))
    .map((q) => q.id);

  const picked = pool.length <= NEW_BLOCK_SIZE ? pool : sampleUpstream(pool);

  // Upstream sorts the drawn block by official question number, so the block
  // is a random *selection* presented in catalogue order — not a shuffle.
  const byNumber = new Map(catalog.questions.map((q) => [q.id, q.number]));
  return [...picked].sort((a, b) => (byNumber.get(a) ?? "").localeCompare(byNumber.get(b) ?? ""));
}

/**
 * Upstream's draw, kept verbatim: pick a random slot, take it if it has not
 * been taken already, and blank it so it cannot be drawn twice. Equivalent in
 * distribution to a shuffle-and-slice, just more literal about it.
 */
function sampleUpstream(pool: number[]): number[] {
  const remaining: (number | null)[] = [...pool];
  const out: number[] = [];
  while (out.length < NEW_BLOCK_SIZE) {
    const i = Math.floor(Math.random() * remaining.length);
    const id = remaining[i];
    if (id !== null && id !== undefined) {
      out.push(id);
      remaining[i] = null;
    }
  }
  return out;
}

/** Picks out `ids` and keeps their order rather than the catalogue's. */
function inDealtOrder(all: Question[], ids: number[]): Question[] {
  const order = new Map(ids.map((id, i) => [id, i]));
  return all
    .filter((q) => order.has(q.id))
    .sort((a, b) => order.get(a.id)! - order.get(b.id)!);
}

function sameSet(a: number[], b: number[]): boolean {
  const x = [...a].sort((m, n) => m - n);
  const y = [...b].sort((m, n) => m - n);
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

function buildPool(
  catalog: LoadedCatalog,
  topics: Topic[],
  mode: PracticeMode,
  progress: ProgressMap,
  marked: number[],
  query: string,
  settings: Settings,
  newBlock: number[] | null
): Question[] {
  const all = catalog.questions;

  switch (mode?.kind) {
    case "new":
      return newBlock ? inDealtOrder(all, newBlock) : [];

    case "mistakes":
      return all.filter((q) => progress[String(q.id)]?.lastCorrect === false);

    case "marked":
      return inDealtOrder(all, marked);

    case "topic": {
      if (mode.categoryId === null) return [];
      const topic = topics.find((t) => t.id === mode.categoryId);
      if (!topic) return [];
      const ids = new Set(topic.questionIds);
      return all.filter((q) => ids.has(q.id));
    }

    case "search": {
      const needle = query.trim().toLowerCase();
      if (!needle) return [];
      return all.filter((q) => haystack(q, settings).includes(needle));
    }

    case "sheet": {
      const set = catalog.sets.find((s) => s.id === mode.setId);
      if (!set) return [];
      // Keep the sheet's own question order.
      return set.questionIds
        .map((id) => catalog.byId.get(id))
        .filter((q): q is Question => Boolean(q));
    }

    case "all":
    default:
      return all;
  }
}

/** Searchable text: question number plus both displayed languages. */
function haystack(q: Question, settings: Settings): string {
  return [
    q.number,
    pick(q.text, settings.primary),
    settings.secondary ? pick(q.text, settings.secondary) : "",
    ...q.answers.map((a) => pick(a.text, settings.primary)),
  ]
    .join(" ")
    .toLowerCase();
}
